import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question: "How do I know if it's heartbreak or depression after a breakup?",
    answer:
      "Grief fluctuates — there are bad hours and okay hours, and it responds to comfort. Depression doesn't fluctuate. If you've had low mood most of the day, nearly every day, for two weeks or more, and the sadness has spread from the breakup to everything (school, friends, the future), that's depression, not heartbreak. It also comes with the full symptom set: insomnia or oversleeping, appetite changes, fatigue, trouble concentrating, and in some cases thoughts of not wanting to be here.",
  },
  {
    question: "Can a breakup really cause a first depressive episode?",
    answer:
      "A breakup doesn't create depression out of nothing. It uncovers a vulnerability that was already there — often genetic, often previously masked by the structure and routine of the relationship. The late teens and early twenties are when most people have their first serious relationship and also when depression is most likely to appear for the first time. Those two facts collide constantly.",
  },
  {
    question: "What if therapy and medication haven't worked after a breakup?",
    answer:
      "If you've had two or more adequate trials of antidepressants without remission, that's treatment-resistant depression. The chance that the next medication produces remission is around 14 percent, and each trial takes six to twelve weeks. SAINT works on the brain circuitry directly — using an fMRI to map your individual brain networks and stimulating the underactive mood-regulation region — which is why it can work when medications haven't.",
  },
  {
    question: "Does SAINT TMS fit a college student's schedule?",
    answer:
      "Yes. Standard TMS requires one session a day, five days a week, for six to nine weeks — 30 to 36 visits that are nearly impossible mid-semester. SAINT compresses the entire course into five consecutive days: ten 10-minute sessions per day with 50-minute breaks in between. It fits inside a winter break, spring break, or the first week of summer.",
  },
  {
    question: "Do I have to stop my medications to do SAINT?",
    answer:
      "No. Most patients continue their existing medication regimen through SAINT treatment. SAINT is not a replacement for what you've built — it is a different kind of intervention aimed at the brain circuitry your medications may not have reached. Nothing enters the bloodstream, and there's no interaction with existing medications. Never change or stop medication without guidance from your prescribing provider.",
  },
];

const related: RelatedLink[] = [
  {
    title: "Can't Stop Crying for No Reason?",
    href: "/learn/cant-stop-crying-for-no-reason",
    description: "What frequent crying can mean and when to get help.",
  },
  {
    title: "Why Am I So Tired All the Time? Depression Fatigue",
    href: "/learn/always-tired-depression-fatigue",
    description: "How depression drains energy after a loss.",
  },
  {
    title:
      "Depression in College: Why the 'Best Years of Your Life' Can Be the Hardest",
    href: "/depression-in-college",
    description:
      "Why depression often starts in college, warning signs for students and parents, and what actually works.",
  },
  {
    title:
      "High-Functioning Depression: When Holding It Together Is the Problem",
    href: "/high-functioning-depression-when-holding-it-together-is-the-problem",
    description:
      "When functioning hides the severity of depression — and why partial relief isn't the goal.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How the five-day, fMRI-guided protocol works.",
  },
];

export default function BreakupDepression() {
  return (
    <DepressionContentPage
      seo={{
        title: "When a Breakup Doesn't Get Better: Heartbreak or Depression?",
        description:
          "Why a breakup at 18–25 can trigger a first depressive episode, how to tell heartbreak from depression, and what to do when therapy and medication aren't enough.",
        canonical: "/when-a-breakup-doesnt-get-better",
        keywords:
          "breakup depression, post-breakup depression, heartbreak vs depression, treatment-resistant depression young adult, SAINT TMS breakup, college breakup depression, first depressive episode, TMS for young adults",
      }}
      badge="For Young Adults"
      heroTitle="When a Breakup Doesn't Get Better: Heartbreak, Depression, and the Line Between Them"
      heroSubtitle="For most people, the pain of a breakup fades. For some, it doesn't — and it becomes something else. Here's how to tell the difference, why breakups at 18 to 25 are a particularly common trigger for a first depressive episode, and what to do when the usual advice stops working."
      lastUpdated="2026-11-03"
      sections={[
        {
          body: (
            <div className="space-y-5">
              <p>
                Everyone has been told the same thing after a breakup: it hurts
                now, but it gets better. Give it a few weeks. Stay busy. You'll
                be fine.
              </p>
              <p>
                For most people, that's true. The pain is real, and then it
                fades, and life reorganizes itself around the space the person
                left.
              </p>
              <p>
                But for some people, it doesn't fade. Weeks become months. The
                sadness stops being about the ex and becomes about everything.
                Sleep goes, concentration goes, appetite goes, and the future
                starts to look like a flat gray line. Friends stop asking about
                it because it's been too long. And the person in the middle of
                it starts to wonder whether something is actually wrong with
                them.
              </p>
              <p>
                This article is for that person, and for the people who love
                them. It's about the difference between heartbreak and
                depression, why a breakup at 18 to 25 is a particularly common
                trigger for a first depressive episode, and what to do when the
                usual advice stops working.
              </p>
            </div>
          ),
        },
        {
          heading: "Why breakups hit this age group so hard",
          body: (
            <div className="space-y-5">
              <p>
                The late teens and early twenties are when most people have
                their first serious relationship, and also when depression is
                most likely to appear for the first time. Those two facts
                collide constantly.
              </p>
              <p>
                A first serious relationship is often the first time a young
                person has organized their identity around another person. Their
                routines, their social circle, their plans, sometimes their
                sense of worth. When it ends, they don't just lose a partner.
                They lose the scaffolding.
              </p>
              <p>
                Add the specific conditions of college: living away from home, a
                compressed social world where you still see your ex in the
                dining hall, an academic load that doesn't pause for grief, and
                a culture where everyone seems to be handling things
                effortlessly. The setup for a downward spiral is nearly
                complete.
              </p>
            </div>
          ),
        },
        {
          heading: "Heartbreak versus depression",
          body: (
            <div className="space-y-5">
              <p>
                Grief after a breakup is normal and expected. It looks like
                sadness, crying, replaying conversations, missing the person,
                and a temporary dip in motivation. Critically, it fluctuates.
                There are bad hours and okay hours. It responds to comfort. It's
                about the loss.
              </p>
              <p>Depression is different in three ways:</p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>It doesn't lift.</strong> Two weeks or more of
                  persistent low mood, most of the day, nearly every day, that
                  isn't relieved by time with friends, distraction, or rest.
                </li>
                <li>
                  <strong>It spreads.</strong> The sadness detaches from the
                  breakup and attaches to everything. School feels pointless.
                  Friends feel exhausting. The future feels closed. Nothing is
                  enjoyable, including things that had nothing to do with the
                  relationship.
                </li>
                <li>
                  <strong>It comes with the full symptom set.</strong> Insomnia
                  or oversleeping. Appetite change. Trouble concentrating, so
                  reading and studying become impossible. Fatigue. Guilt or
                  worthlessness that's out of proportion. And in some cases,
                  thoughts of not wanting to be here.
                </li>
              </ul>
              <p>
                A breakup that triggers this picture didn't cause depression out
                of nothing. It uncovered a vulnerability that was already there,
                often genetic, often previously masked by structure and routine.
              </p>
            </div>
          ),
        },
        {
          heading: "Signs to watch for in yourself or a friend",
          body: (
            <div className="space-y-3">
              <ul className="list-disc pl-5 space-y-2">
                <li>It's been more than a month and nothing is better</li>
                <li>Grades are slipping and you can't make yourself care</li>
                <li>
                  You've stopped going out, or you go out and feel nothing
                </li>
                <li>
                  You're using alcohol, cannabis, or other substances to sleep
                  or to numb
                </li>
                <li>You can't concentrate long enough to read a page</li>
                <li>You feel like a burden to the people around you</li>
                <li>
                  You've thought that everyone would be better off without you
                </li>
              </ul>
              <p className="pt-2 font-semibold text-primary">
                That last one is an emergency, not a phase. Call or text 988.
              </p>
            </div>
          ),
        },
        {
          heading: "The first steps",
          body: (
            <div className="space-y-5">
              <p>
                Campus counseling, or a therapist in the community, is the right
                place to start. Talk therapy for post-breakup depression works
                well for many people, especially when it starts early. If
                symptoms are moderate to severe, a doctor may add an
                antidepressant.
              </p>
              <p>
                Do the basics too, even when they feel pointless: sleep on a
                schedule, eat real food, move your body, and cut back on alcohol
                and drugs, which reliably deepen depression. Tell someone. A
                friend, a parent, an RA. Isolation is both a symptom and an
                accelerant.
              </p>
            </div>
          ),
        },
        {
          heading: "When the first steps don't work",
          body: (
            <div className="space-y-5">
              <p>
                Here's the part that isn't in most breakup advice. Some people
                do the therapy, take the medication, wait the eight weeks, and
                are still depressed. Then they try a second medication and wait
                another eight weeks. By the time they've been through two
                adequate trials without relief, they're a semester or a year
                further in, and the depression has a name: treatment-resistant
                depression.
              </p>
              <p>
                For someone in that position, the chance that the next
                medication produces remission is around 14 percent.
              </p>
              <p>
                Antidepressants work on brain chemistry. But depression is also
                a circuitry problem. The region behind the forehead that
                regulates mood, motivation, and attention is underactive in
                depression, and medication doesn't directly retrain it.
                Transcranial magnetic stimulation (TMS) does, using focused
                magnetic pulses through a coil on the scalp. No anesthesia,
                nothing entering your bloodstream.
              </p>
            </div>
          ),
        },
        {
          heading: "Why SAINT makes sense for a young adult",
          body: (
            <div className="space-y-4">
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an accelerated, FDA-cleared form of TMS developed at
                Stanford's Brain Stimulation Lab. It's cleared for adults 18 and
                older with major depressive disorder who haven't gotten adequate
                relief from antidepressants in their current episode.
              </p>
              <p>
                The reason it fits a college student's life is the schedule.
                Standard TMS is one visit a day for six to nine weeks, which is
                nearly impossible mid-semester. SAINT takes five days. Here's
                how it works at a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation.</strong> Our clinical team reviews your
                  history, what you've tried, and confirms you're a candidate.
                  The initial consultation is a Zoom call; as steps evolve,
                  you'll also have the option to meet with our psychiatrist by
                  Zoom web conference or in person. The mandatory in-person
                  steps include fMRI mapping and the actual treatment. Parents
                  are welcome if you want them there; the decision is yours.
                </li>
                <li>
                  <strong>fMRI brain scan.</strong> A roughly 45-minute
                  functional MRI maps how your individual brain networks are
                  wired.
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location for
                  stimulation. Neuronavigation guides the coil there for every
                  session.
                </li>
                <li>
                  <strong>Five-day stimulation series.</strong> Ten 10-minute
                  sessions per day, with 50-minute breaks in between, for five
                  consecutive days. It fits inside a break.
                </li>
              </ol>

              <SaintComparisonStatBand />

              <p>
                In the clinical trials behind SAINT's clearance, 79 percent of
                participants reached remission, with an average time to
                remission of 2.6 days. Across more than 400 trial patients, 85
                percent experienced symptom relief, with no serious adverse
                events. Side effects were generally mild: headache, fatigue, and
                temporary scalp discomfort. You don't have to stop your current
                medications. SAINT isn't appropriate for people with
                non-removable metal in or near the head or an implanted device.
              </p>
            </div>
          ),
        },
        {
          heading: "For parents watching from a distance",
          body: (
            <div className="space-y-5">
              <p>
                If your child's breakup was months ago and they still sound
                flat, tired, or vague on the phone, trust that instinct. Ask
                directly what it's like right now, and listen without trying to
                fix it. If they've already done counseling and medication
                without much change, you're allowed to look beyond what the
                campus offers.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                If the breakup was a while ago and you're still not okay, that
                isn't weakness and it isn't a failure to move on. It may be
                depression, and depression is treatable.
              </p>
              <p>
                At Neuro Wellness TMS Centers of America, the first appointment
                is an evaluation, not a commitment. We offer private outpatient
                care with a dedicated care team and weekend availability. We'll
                tell you honestly whether SAINT is a fit. Insurance coverage
                varies by plan, and many students remain on a parent's
                insurance; our team can help you understand your options,
                including prior authorization and self-pay.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you are in crisis or thinking about harming yourself, call or
                text 988 (Suicide &amp; Crisis Lifeline) or go to your nearest
                emergency room. This article is for educational purposes and is
                not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
